import invoicesService from '@/services/invoices';
import paymentRepository from '@/services/paymentRepository';
import { extractApiErrorMessage, extractApiSuccessMessage, extractPayload } from '@/utils/dataNormalization';
import { downloadBlobFile } from '@/utils/fileDownload';

export const invoiceDetailMethods = {
  buildPayInvoiceModalRow(invoice = null) {
    const source = invoice && typeof invoice === 'object' ? invoice : (this.invoiceData || {});
    const receiver = this.selectedReceiver && typeof this.selectedReceiver === 'object'
      ? this.selectedReceiver
      : {};
    const sourceBillingData = source?.invoice_billing_data && typeof source.invoice_billing_data === 'object'
      ? source.invoice_billing_data
      : {};
    const sourcePaymentMethod = sourceBillingData?.payment_method && typeof sourceBillingData.payment_method === 'object'
      ? sourceBillingData.payment_method
      : {};
    const invoicePaymentMethodLabel = String(
      sourcePaymentMethod?.label
      || source?.paymentMethodLabel
      || source?.payment_method_label
      || source?.invoice_payment_method_label
      || ''
    ).trim();
    const invoicePaymentMethodCode = String(
      sourcePaymentMethod?.code
      || source?.paymentMethod
      || source?.payment_method
      || source?.invoice_payment_method
      || ''
    ).trim();
    const paymentId = Number(source?.payment_id || source?.payment?.id || 0) || 0;
    const customerId = Number(source?.customer_id || source?.company_id || 0) || 0;
    const total = Number(source?.balance || source?.total || 0) || 0;

    if (paymentId <= 0 || customerId <= 0 || total <= 0) {
      return null;
    }

    return {
      id: paymentId,
      customer_id: customerId,
      company_id: customerId,
      customer_name: String(source?.customer_name || source?.company_name || '').trim(),
      company_name: String(source?.company_name || source?.customer_name || '').trim(),
      date: String(source?.date || '').trim(),
      currency: String(source?.currency || 'MXN').trim().toUpperCase() || 'MXN',
      exchange_rate: Number(source?.exchange_rate || 1) || 1,
      total,
      subtotal: total,
      invoice_id: Number(source?.id || 0) || 0,
      invoice: `${String(source?.serial || '').trim()}${String(source?.folio || '').trim()}`.trim(),
      voucher_type: 'cfdi',
      special: 0,
      contributor_id: Number(receiver?.meta?.id || receiver?.id || 0) || 0,
      invoice_contributor_id: Number(receiver?.meta?.id || receiver?.id || 0) || 0,
      invoice_payment_method_label: invoicePaymentMethodLabel,
      invoice_payment_method: invoicePaymentMethodCode,
      invoice_billing_data: {
        contributor: {
          id: Number(receiver?.meta?.id || receiver?.id || 0) || 0,
          rfc: String(receiver?.meta?.rfc || receiver?.rfc || '').trim(),
        },
        payment_method: {
          label: invoicePaymentMethodLabel,
          code: invoicePaymentMethodCode,
        },
      },
    };
  },
  openPayInvoiceModal() {
    const row = this.buildPayInvoiceModalRow(this.invoiceData);
    if (!row) {
      this.$toast?.error(String(this.text?.toasts?.openDetailError || 'No se pudo identificar el plan de pago para esta factura.'));
      return;
    }

    this.payInvoiceRow = row;
    this.showPayInvoiceModal = true;
  },
  closePayInvoiceModal(force = false) {
    if (this.payInvoiceSubmitting && !force) {
      return;
    }

    this.showPayInvoiceModal = false;
    this.payInvoiceRow = null;
  },
  async submitPayInvoiceModal(payload) {
    this.payInvoiceSubmitting = true;

    try {
      const paymentPlanId = Number(payload?.payment_plan_id || 0);
      if (paymentPlanId <= 0) {
        this.$toast?.error('No se pudo identificar el plan de pago para registrar el cobro.');
        return;
      }

      const response = await paymentRepository.payPlan(paymentPlanId, payload);
      const apiErrorMessage = extractApiErrorMessage(response, 'No fue posible registrar el pago de la factura.');
      if (apiErrorMessage) {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      this.$toast?.success(extractApiSuccessMessage(response, 'Pago de factura registrado correctamente.'));
      const responsePayload = extractPayload(response);
      const createdPaymentReceiptId = Number(
        responsePayload?.payment_receipt?.id
        || responsePayload?.payment_receipt_id
        || 0
      ) || 0;

      this.closePayInvoiceModal(true);

      if (createdPaymentReceiptId > 0) {
        this.$router.push({
          name: 'paymentReceiptDetail',
          params: {
            paymentReceiptId: String(createdPaymentReceiptId),
          },
        }).catch(() => {});
        return;
      }

      await this.loadInvoiceDetail();
    } catch (error) {
      this.$toast?.error(error?.response?.data?.message || error?.message || 'No fue posible registrar el pago de la factura.');
    } finally {
      this.payInvoiceSubmitting = false;
    }
  },
  resolveInvoiceSnapshotContext() {
    const fromRouteState = this.$route?.state?.invoiceSnapshot;

    let fromStorage = null;
    try {
      const storageKey = `invoice-detail-${this.invoiceId}`;
      const raw = sessionStorage.getItem(storageKey);
      fromStorage = raw ? JSON.parse(raw) : null;
    } catch (error) {
      fromStorage = null;
    }

    return {
      ...(fromStorage && typeof fromStorage === 'object' ? fromStorage : {}),
      ...(fromRouteState && typeof fromRouteState === 'object' ? fromRouteState : {}),
    };
  },
  initializeDetailPage() {
    this.loadInvoiceDetail();
    document.addEventListener('click', this.handleDocumentClick);
  },
  cleanupDetailPage() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  handleDocumentClick(event) {
    const target = event?.target;
    if (target && target.closest('.app-detail-more-actions-wrap')) {
      return;
    }

    this.showMoreActionsMenu = false;
  },
  toggleMoreActionsMenu() {
    this.showMoreActionsMenu = !this.showMoreActionsMenu;
  },
  handlePrimaryHeaderAction(action, onAction) {
    this.showMoreActionsMenu = false;
    onAction(action);
  },
  handleSecondaryHeaderAction(action, onAction) {
    this.showMoreActionsMenu = false;
    onAction(action);
  },
  async loadInvoiceDetail() {
    if (this.invoiceId > 0) {
      try {
        const response = await invoicesService.show(this.invoiceId);
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage !== '') {
          this.$toast?.error(apiErrorMessage);
          this.$router.replace({ name: 'invoices' });
          return;
        }

        const payload = extractPayload(response);
        if (payload && typeof payload === 'object' && Number(payload?.id || 0) > 0) {
          const mergedPayload = {
            ...this.resolveInvoiceSnapshotContext(),
            ...payload,
          };

          this.setInvoiceData(mergedPayload);

          try {
            sessionStorage.setItem(`invoice-detail-${this.invoiceId}`, JSON.stringify(mergedPayload));
          } catch (error) {
            // Ignore storage access errors.
          }

          return;
        }
      } catch (error) {
        const fallback = this.text.detail.toast.loadInvoiceError;
        const message = extractApiErrorMessage(error?.response || error, fallback);
        if (String(message || '').trim() !== '') {
          this.$toast?.error(message);
        }
      }
    }

    this.loadInvoiceSnapshot();
  },
  loadInvoiceSnapshot() {
    const stateSnapshot = this.$route?.state?.invoiceSnapshot;
    if (stateSnapshot && typeof stateSnapshot === 'object') {
      this.setInvoiceData(stateSnapshot);
      return;
    }

    try {
      const storageKey = `invoice-detail-${this.invoiceId}`;
      const raw = sessionStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') {
        this.setInvoiceData(parsed);
        return;
      }
    } catch (error) {
      // Ignore storage parse errors and fallback to redirect.
    }

    this.$toast?.error(this.text.detail.toast.missingInvoiceData);
    this.$router.replace({ name: 'invoices' });
  },
  setInvoiceData(payload) {
    this.invoiceData = payload;

    const serialLabel = String(payload?.serial || '').trim();
    const serialValue = payload?.serialId || serialLabel || 0;
    const folio = String(payload?.folio || '').trim();
    this.serialOptions = serialLabel !== ''
      ? [{ value: serialValue, label: serialLabel, meta: { folio } }]
      : [];

    const paymentMethodCode = String(payload?.paymentMethod || '').trim();
    const paymentMethodLabel = String(payload?.paymentMethodLabel || paymentMethodCode).trim();
    this.paymentMethodOptions = paymentMethodCode !== ''
      ? [{ value: paymentMethodCode, label: paymentMethodLabel }]
      : [];

    const paymentFormCode = String(payload?.paymentForm || '').trim();
    const paymentFormLabel = String(payload?.paymentFormLabel || paymentFormCode).trim();
    this.paymentFormOptions = paymentFormCode !== ''
      ? [{ value: paymentFormCode, label: paymentFormLabel }]
      : [];
  },
  onHeaderAction(action) {
    const actionKey = String(action?.key || '').trim();

    if (actionKey === 'pay') {
      this.openPayInvoiceModal();
      return;
    }

    if (actionKey === 'download') {
      this.downloadInvoiceZip();
      return;
    }

    if (actionKey === 'send') {
      this.openSendEmailModal();
      return;
    }

    if (actionKey === 'clone') {
      if (this.invoiceId <= 0) {
        this.$toast?.error(String(this.text?.detail?.toast?.missingInvoiceData || ''));
        return;
      }

      this.$router.push({
        name: 'invoiceCreate',
        query: {
          clone_invoice_id: String(this.invoiceId),
        },
        state: {
          cloneInvoiceSnapshot: this.invoiceData && typeof this.invoiceData === 'object'
            ? { ...this.invoiceData }
            : null,
        },
      }).catch(() => {});
      return;
    }

    if (actionKey === 'downloadPdf') {
      this.previewInvoicePdf();
      return;
    }

    if (actionKey === 'downloadXml') {
      this.downloadInvoiceXml();
      return;
    }

    const actionToasts = this.text?.detail?.toast?.actions || {};
    const toastMessage = String(actionToasts?.[actionKey] || '').trim();

    if (toastMessage !== '') {
      this.$toast?.info(toastMessage);
    }
  },
  buildInvoiceDocumentBaseName() {
    const serial = String(this.invoiceData?.serial || '').trim();
    const folio = String(this.invoiceData?.folio || '').trim();
    const uuid = String(this.invoiceData?.uuid || '').trim().toUpperCase();
    const reference = `${serial}${folio}`.trim() || 'factura';

    const safeReference = reference
      .replace(/\s+/g, '_')
      .replace(/[^A-Za-z0-9_\-]/g, '') || 'factura';

    const safeUuid = uuid.replace(/[^A-Z0-9\-]/g, '');
    return safeUuid !== '' ? `${safeReference}_${safeUuid}` : safeReference;
  },
  openPdfBlob(payload) {
    const blob = payload instanceof Blob
      ? payload
      : new Blob([payload], { type: 'application/pdf' });

    const objectUrl = window.URL.createObjectURL(blob);
    const opened = window.open(objectUrl, '_blank', 'noopener');

    if (!opened) {
      downloadBlobFile(blob, `${this.buildInvoiceDocumentBaseName()}.pdf`, 'application/pdf');
    }

    window.setTimeout(() => {
      window.URL.revokeObjectURL(objectUrl);
    }, 60 * 1000);
  },
  async previewInvoicePdf() {
    if (this.invoiceId <= 0) {
      this.$toast?.error(String(this.text?.detail?.toast?.missingInvoiceData || ''));
      return;
    }

    try {
      const response = await invoicesService.preview(this.invoiceId);
      this.openPdfBlob(response?.data ?? response);
    } catch (error) {
      const fallback = String(this.text?.detail?.toast?.actions?.downloadPdf || this.text?.detail?.toast?.actions?.download || '');
      const message = extractApiErrorMessage(error?.response || error, fallback);
      this.$toast?.error(message || fallback);
    }
  },
  async downloadInvoiceZip() {
    if (this.invoiceId <= 0) {
      this.$toast?.error(String(this.text?.detail?.toast?.missingInvoiceData || ''));
      return;
    }

    try {
      const response = await invoicesService.downloadZip(this.invoiceId);
      downloadBlobFile(response?.data ?? response, `${this.buildInvoiceDocumentBaseName()}_cfdi.zip`, 'application/zip');
    } catch (error) {
      const fallback = String(this.text?.detail?.toast?.actions?.download || '');
      const message = extractApiErrorMessage(error?.response || error, fallback);
      this.$toast?.error(message || fallback);
    }
  },
  async downloadInvoicePdf() {
    if (this.invoiceId <= 0) {
      this.$toast?.error(String(this.text?.detail?.toast?.missingInvoiceData || ''));
      return;
    }

    try {
      const response = await invoicesService.downloadPdf(this.invoiceId);
      downloadBlobFile(response?.data ?? response, `${this.buildInvoiceDocumentBaseName()}.pdf`, 'application/pdf');
    } catch (error) {
      const fallback = String(this.text?.detail?.toast?.actions?.downloadPdf || '');
      const message = extractApiErrorMessage(error?.response || error, fallback);
      this.$toast?.error(message || fallback);
    }
  },
  async downloadInvoiceXml() {
    if (this.invoiceId <= 0) {
      this.$toast?.error(String(this.text?.detail?.toast?.missingInvoiceData || ''));
      return;
    }

    try {
      const response = await invoicesService.downloadXml(this.invoiceId);
      downloadBlobFile(response?.data ?? response, `${this.buildInvoiceDocumentBaseName()}.xml`, 'application/xml');
    } catch (error) {
      const fallback = String(this.text?.detail?.toast?.actions?.downloadXml || '');
      const message = extractApiErrorMessage(error?.response || error, fallback);
      this.$toast?.error(message || fallback);
    }
  },
};
